/**
 * Launch Bundle Banner — slim gold strip that sits ABOVE the sticky nav.
 *
 * Fetches the bundle SKU (`launch-bundle-spirit-elite`), checks the
 * server-computed `on_sale` + `sale_expires_at`, and renders only while the
 * sale is live. Once the 48-hour flash window elapses, the backend's
 * `_sale_is_live()` guard flips `on_sale` to null on the next TTL refresh
 * and this banner auto-hides — no admin action needed.
 *
 * Height: 40 px on desktop, 44 px on mobile (per spec). Sits above the
 * sticky header so both stay visible while scrolling.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

const BUNDLE_SLUG = "launch-bundle-spirit-elite";

export const LaunchBundleBanner = () => {
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/products/${BUNDLE_SLUG}`)
      .then((r) => { if (!cancelled) setBundle(r.data); })
      .catch(() => { /* silent — banner just won't render */ });

    // Also re-poll every 60 s so the banner disappears within one minute
    // of the sale expiring, even without a page reload.
    const iv = setInterval(() => {
      api
        .get(`/products/${BUNDLE_SLUG}`)
        .then((r) => { if (!cancelled) setBundle(r.data); })
        .catch(() => {});
    }, 60_000);

    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  if (!bundle || !bundle.on_sale) return null;

  const salePrice = bundle.price ?? bundle.price_usd;
  const retail = bundle.original_price ?? bundle.original_price_usd ?? 19.98;
  const savings = Math.max(0, Math.round(retail - salePrice));

  return (
    <div
      data-testid="launch-bundle-banner"
      className="bg-[#0A192F] text-white border-b border-[#D4AF37]/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-2 sm:py-2.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
        <div className="flex items-center gap-2 flex-wrap justify-center">
          <Sparkles size={14} className="text-[#D4AF37] shrink-0" />
          <span
            data-testid="launch-bundle-headline"
            className="font-accent text-[10px] sm:text-[11px] tracking-[0.18em] text-[#D4AF37]"
          >
            LAUNCH BUNDLE SPECIAL
          </span>
          <span className="text-white/40 hidden sm:inline">·</span>
          <span className="text-[11px] sm:text-xs text-white/90">
            Grab <strong className="text-[#D4AF37]">BOTH</strong> The Spirit Realm + The Elite Negotiator for{" "}
            <strong data-testid="launch-bundle-price" className="text-[#D4AF37]">
              ${salePrice.toFixed(2)}
            </strong>
            {savings > 0 && (
              <span className="text-white/70"> (save ${savings})</span>
            )}
          </span>
        </div>
        <Link
          data-testid="launch-bundle-cta"
          to={`/checkout/${BUNDLE_SLUG}`}
          className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#0A192F] font-accent text-[10px] sm:text-[11px] tracking-widest px-3 py-1.5 rounded-full hover:bg-[#E5C158] transition shadow-[0_1px_4px_rgba(212,175,55,0.35)] whitespace-nowrap"
        >
          GET THE BUNDLE
          <ArrowRight size={12} strokeWidth={2.5} />
        </Link>
      </div>
    </div>
  );
};
